import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteBatch } from '../../../hooks/useBatchMutations';
import type { Batch } from '../../../models/Batch';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithBatch: (batch: Batch) => void;
}

const DeleteBatchModal = forwardRef<ExtendedModalCenterRef, object>((_, ref) => {
  const { t } = useTranslation('batchesModule');
  const { toast } = useToast();
  const deleteBatch = useDeleteBatch();
  const internalModalRef = useRef<ModalCenterRef>(null);

  const [batch, setBatch] = useState<Batch | null>(null);

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    setBatch(null);
  };

  const handleDelete = async () => {
    if (!batch) return;

    try {
      await deleteBatch.mutateAsync(batch.id);
      toast({ title: t('toast.deleteSuccess'), variant: 'default' });
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('toast.deleteError'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const openWithBatch = useCallback((batchData: Batch) => {
    setBatch(batchData);
    internalModalRef.current?.openModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithBatch,
    }),
    [openWithBatch],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modals.delete.title')} type="error">
      <div className="space-y-4">
        <p className="text-sm text-text">{t('modals.delete.message', { batchNumber: batch?.batch_number })}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={deleteBatch.isPending}>
            {t('modals.delete.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="text-white"
            onClick={handleDelete}
            disabled={deleteBatch.isPending}
          >
            {t('modals.delete.submit')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

DeleteBatchModal.displayName = 'DeleteBatchModal';

export default DeleteBatchModal;
